<script lang="ts" setup>
import { onBeforeMount, ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import RR from "@fullcalendar/rrule";
import { RRule } from 'rrule';
import { useChecksStore, useSettingsStore } from '@/stores/';
import type { CalendarOptions } from 'fullcalendar/index.js';
import RecordView from '@/components/Calendar/RecordView.vue'
import { getLastDayOfWeek, hasSpecificDateConstraint, isLastWorkingDayOfYear } from "@/utils"

const checkStore = useChecksStore();
const settingStore = useSettingsStore();
const recordid = ref(0)
const calendar = ref();
const drawerPage = ref(false);
const events = ref([] as any[]);
const componentKey = ref(0);
const weekdays = [ "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const startDate = ref(new Date());
const endDate = ref(new Date(new Date().getFullYear(), new Date().getMonth(), startDate.value.getDate() + 1));
let eventGuid = 0;
let loading = true;

onBeforeMount(async () =>{
    if (settingStore.settings == null) await settingStore.get();
})

function getMondayOfWeek(d: Date) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date;
}
function isSameWeek(d1: Date, d2: Date) {
    return getMondayOfWeek(d1).getTime() === getMondayOfWeek(d2).getTime();
}
function isSameMonth(d1: Date, d2: Date) {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
}
function isSameYear(d1: Date, d2: Date) {
    return d1.getFullYear() === d2.getFullYear();
}

const eventContent = (evt:any) => {
    return {html: `
        <div class="w-full calendar-item ${evt.event.extendedProps.complete==true ? "bg-green-600" : ""} ${evt.event.extendedProps.complete==false ? "bg-red-600" : ""}" title="${evt.event._def.title}">
            <div class="flex items-center px-2  w-full py-3 border-b border-surface-300">
                <div class='icon mr-2' style='background: ${evt.event.extendedProps.icon.colour};'>
                    <i class="${evt.event.extendedProps.icon.icon}"></i>
                    ${evt.event.extendedProps.issue == true ? '<i class="fas fa-exclamation-triangle float text-xs text-orange-500"></i>' : ""}
                </div>
                <div class="check-icon mr-1">
                    ${evt.event.extendedProps.complete == true ? '<span class="">Complete</span>' : ""}
                    ${evt.event.extendedProps.complete == false ? '<span class="">Missed</span>' : ""}
                </div>
            </div>
            <div class="overflow text-base">
                ${evt.event.title}
            </div>
        </div>
    `};
}
const showEvent = (id:number) => {
    recordid.value = id
    drawerPage.value = true;
}

const calendarOptions = ref({
    plugins: [ dayGridPlugin, RR],
    initialView: 'dayGridDay',
    firstDay: 1,
    titleFormat: { year: 'numeric', day:"numeric", month: 'long' },
    contentHeight:window.innerHeight - 250,
    events: events.value,
    eventClick: function(info:any) {
        if (info.event.extendedProps.complete) {
            showEvent(info.event.extendedProps.completedcheckid);
        }
    },
    eventContent: eventContent,
    eventsSet: function() {
        if (loading) return;
        const range = calendar.value.calendar.currentData.dateProfile.currentRange;
        if (new Date(range.start).getTime() !== startDate.value.getTime() ||
            new Date(range.end).getTime() !== endDate.value.getTime()) {
            startDate.value = range.start;
            endDate.value = range.end;
            getEvents();
        }
    },
    dayHeaderContent: (arg:any) => {
        return {html: `
            <div class='calendar-header'>
                <div class='calendar-header-dayname'>${weekdays[arg.date.getDay()]}</div>
            </div>
        `}
    },
    hiddenDays:(settingStore.settings?.calendarType === 'Full Week' ? [] : [0,6]),
    headerToolbar: {
        start: 'today prev,next title',
        center: '',
        right: ''
    },
} as CalendarOptions);

watch(() => settingStore.settings?.calendarType, () => {
    calendarOptions.value.hiddenDays = settingStore.settings!.calendarType === 'Full Week' ? [] : [0,6];
    calendar.value.calendar.render();
});

const dateFromString = (date: Date) =>
    `${date.getFullYear()}${String(date.getMonth()+1).padStart(2,'0')}${String(date.getDate()).padStart(2,'0')}`;
const timeFromString = (date:Date) => {
    var hours = String(date.getHours()).padStart(2, '0');
    var minutes = String(date.getMinutes()).padStart(2, '0');
    var seconds = String(date.getSeconds()).padStart(2, '0');
    return hours+minutes+seconds;
}
function subtractDate(date:Date, days:number) {
    date.setDate(date.getDate() - days);
    return date;
}
function createEventId() {
    return String(eventGuid++)
}
function addDate(date: Date, days: number) {
    date.setDate(date.getDate() + days);
    return date;
}
    //  If a specific day is not set for a recurance rule we infer it
    const buildAdjustedRRule = (ruleStr:string, isFullWeek:boolean) => {
        // Extract DTSTART if present
        let dtstart: Date | undefined;
        let rrulePart = ruleStr;

        const dtstartMatch = ruleStr.match(/DTSTART:(\d{8})T(\d{6})/);
        if (dtstartMatch) {
            const [, dateStr, timeStr] = dtstartMatch;
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1;
            const day = parseInt(dateStr.substring(6, 8));
            const hour = parseInt(timeStr.substring(0, 2));
            const minute = parseInt(timeStr.substring(2, 4));
            const second = parseInt(timeStr.substring(4, 6));
            dtstart = new Date(Date.UTC(year, month, day, hour, minute, second));

            // Remove DTSTART line for parsing
            rrulePart = ruleStr.replace(/DTSTART:[^\n]*\n?/, '');
        }

        const parsed = RRule.fromString(rrulePart);
        const options = { ...parsed.origOptions };

        // Apply the extracted dtstart
        if (dtstart) {
            options.dtstart = dtstart;
        }

        let requiresYearEndFilter = false;
        if (hasSpecificDateConstraint(ruleStr)) {
            return { rrule: new RRule(options), requiresYearEndFilter };
        }

        switch (options.freq) {
            case RRule.WEEKLY: {              
                options.byweekday = [getLastDayOfWeek(isFullWeek)];
                break;
            }

            case RRule.MONTHLY: {
                if (isFullWeek) {
                    options.bymonthday = [-1];
                } else {
                    delete options.bymonthday;
                    options.byweekday = [
                        RRule.MO,
                        RRule.TU,
                        RRule.WE,
                        RRule.TH,
                        RRule.FR
                    ];
                    options.bysetpos = -1;
                }
                break;
            }

            case RRule.YEARLY: {
                if (isFullWeek) {
                    options.bymonth = [12];
                    options.bymonthday = [31];
                } else {
                    options.bymonth = [12];
                    options.bymonthday = [29, 30, 31];
                    options.byweekday = [
                        RRule.MO,
                        RRule.TU,
                        RRule.WE,
                        RRule.TH,
                        RRule.FR
                    ];
                    requiresYearEndFilter = true;
                }
                break;
            }
        }

        return { rrule: new RRule(options), requiresYearEndFilter };
    }

    const getEvents = async () => {
        events.value = [];
        const isFullWeek = settingStore.settings!.calendarType === 'Full Week';
        loading = true;
        eventGuid = 0;
        await checkStore.getCheckCalendar(
            `${startDate.value.getFullYear()}-${startDate.value.getMonth()+1}-${startDate.value.getDate()}`,
            `${endDate.value.getFullYear()}-${endDate.value.getMonth()+1}-${endDate.value.getDate()}`
        );

        const today = new Date();
        checkStore.calendar.forEach((o) => {
            if (o.recurranceRule !== '') {
                const s = o.dueTime!.split(':');
                const createdOn = new Date(o.createdOn!);
                const dtstart = createdOn > startDate.value
                    ? `DTSTART:${dateFromString(addDate(new Date(o.createdOn!), 1))}T${timeFromString(createdOn)}`
                    : `DTSTART:${dateFromString(startDate.value)}T000000`;

                //  If the check was deleted we want it to show up until the time it was deleted
                const ruleend = o.deletedOn != null ? new Date(o.deletedOn) : null;
                let { rrule, requiresYearEndFilter } = buildAdjustedRRule(dtstart + '\n' + o.recurranceRule, isFullWeek);
                if (ruleend) rrule = new RRule({ ...rrule.origOptions, until: ruleend });

                let ruleStart = startDate.value > createdOn ? startDate.value : createdOn;
                ruleStart = subtractDate(new Date(ruleStart.getFullYear(), ruleStart.getMonth(), ruleStart.getDate()), 1);

                const isWeeklyRule = rrule.options.freq === RRule.WEEKLY;
                const isMonthlyRule = rrule.options.freq === RRule.MONTHLY;
                const isYearlyRule = rrule.options.freq === RRule.YEARLY;
                rrule.between(ruleStart, endDate.value).forEach((date) => {
                    if (requiresYearEndFilter && !isLastWorkingDayOfYear(date)) return;
                    date.setHours(parseInt(s[0]), parseInt(s[1]), 0, 0);

                    // For weekly/monthly/yearly: if completed anywhere in the period, show as completed on the scheduled date
                    if (isWeeklyRule || isMonthlyRule || isYearlyRule) {
                        const periodCompletionKey = Object.keys(o.completions!).find((x) => {
                            const cd = new Date(x);
                            if (cd < startDate.value || cd > endDate.value) return false;
                            if (isWeeklyRule) return isSameWeek(cd, date);
                            if (isMonthlyRule) return isSameMonth(cd, date);
                            return isSameYear(cd, date);
                        });
                        if (periodCompletionKey) {
                            const completedcheckid = o.completions![periodCompletionKey].id;
                            const issue = o.completions![periodCompletionKey].hadIssue;
                            const alreadyAdded = events.value.some((x: any) => x.start.getTime() === date.getTime() && x.checkid === o.id);
                            if (!alreadyAdded && (ruleend == null || date <= ruleend)) {
                                events.value.push({ id: createEventId(), title: o.name, start: date, icon: o.icon, complete: true, checkid: o.id, completedcheckid, issue });
                            }
                            return;
                        }
                    }

                    const completions = Object.keys(o.completions!).filter(x => new Date(x).toLocaleDateString() === date.toLocaleDateString());
                    const completedcheckid = completions.length > 0 ? o.completions![completions[0]].id : null;
                    const issue = completions.length > 0 ? o.completions![completions[0]].hadIssue : false;
                    const isComplete: boolean | undefined = completions.length > 0 ? true : (today <= date ? undefined : false);

                    const alreadyAdded = events.value.some((x: any) => x.start.getTime() === date.getTime() && x.checkid === o.id);
                    if (!alreadyAdded && (ruleend == null || date <= ruleend)) {
                        events.value.push({ id: createEventId(), title: o.name, start: date, icon: o.icon, complete: isComplete, checkid: o.id, completedcheckid, issue });
                    }
                });
            }

            // Second loop: add completions on their actual dates (handles inferred-day completed checks)
            Object.keys(o.completions!).forEach((c) => {
                const completedcheckid = o.completions![c].id;
                const completionDate = new Date(c);
                if (completionDate < startDate.value || completionDate > endDate.value) return;
                if (!events.value.some((x: any) => x.completedcheckid === completedcheckid)) {
                    events.value.push({ id: createEventId(), title: o.name, start: completionDate, icon: o.icon, complete: true, checkid: o.id, completedcheckid, issue: o.completions![c].hadIssue });
                }
            });
        });

        calendarOptions.value.initialDate = startDate.value;
        calendarOptions.value.events = events.value;
        componentKey.value++;
        loading = false;
    }
    getEvents()
</script>

<template>
    <div>
        <FullCalendar ref="calendar" :options="calendarOptions" :key="componentKey" />
        <el-drawer
        v-model="drawerPage"
        :append-to-body="true"
        style="min-width: 90%;"
        title="Completed Check"
        :destroy-on-close="true"
        >
            <RecordView :id="recordid"></RecordView>
        </el-drawer>
    </div>
</template>


<style>
.fc h2.fc-toolbar-title {display: inline; font-size: 1.2em; font-weight: 600;}
.fc .fc-header-toolbar {margin-bottom: .5rem!important; font-family: 'Montserrat', sans-serif !important}
.fc button.fc-button, .fc button.fc-button:hover, .fc button.fc-button:active {
    background-color: transparent!important;
    border: 0!important;
    font-size:1.2em;
    color: #000;
    outline: 0;
}
.fc button.fc-today-button{
    font-size: 1em;
    background-color: transparent;
    text-transform:capitalize;
}
.fc button.fc-today-button:disabled{
    color: #aaa;
    background-color: transparent;
}
.fc .fc-col-header-cell-cushion { width: 100%; }
.fc .fc-scroller-harness, .fc .fc-scroller {overflow:visible!important;}
.fc .fc-daygrid-day-events{ margin: 0; }
.fc .fc-daygrid-event-harness,
.fc .fc-col-header-cell-cushion { width: 100%; }
.fc .fc-day-today { position: relative; }
.fc .fc-col-header-cell-cushion {padding:0;}
.fc .fc-daygrid-day.fc-day-today {background: #fff;}
.fc .fc-day-today .calendar-header:before {
    content: ' ';
    width: 100%;
    height: 6px;
    background: #05aef1;
    position: absolute;
    left: 0;
    top: -.4rem;
}
.fc-daygrid-dot-event.fc-event-mirror, .fc-daygrid-dot-event {margin: 0; padding: 0;}
.fc-theme-standard td{ border-bottom: 0; }
.calendar-header {
    width: 100%;
    font-family: 'gibsonlight';
    position: relative;
    padding: .5rem 2rem;
    font-weight: normal;
}
.calendar-header-day {
    font-size: 2.8rem;
    text-align: left;
}
.calendar-header-dayname {
    font-size: 1.1rem;
    color: #000;
    text-align: center;
    font-family: 'Montserrat', sans-serif !important
}
.fc .fc-day-today .calendar-header{ font-weight: 700; font-family: 'gibsonregular';}
.fc-h-event{ background: #fff; border-color: transparent; color: #000;}
.fc-h-event .fc-event-main{color: #000;}
.check-icon{
    position: relative;
    width: 3rem;
}
.check-icon i{
    font-size: 1.5rem;
    position: absolute;
    top: 0rem;
    left: 0.1rem;
}
.check-icon i.base{
    position: static;
    color: #ddd;
}
.check-icon img{
    width: 1.5rem;
    position: absolute;
    top: 0.1rem;
    left: 0.1rem;
}
.icon{
    color: #fff;
    width: 2rem;
    height: 2rem;
    line-height: 2rem;
    text-align: center;
    border-radius: 1.5rem;
    position: relative;
    font-size:1rem;
    min-width:32px;
}
.icon .float{
    position:absolute;
    font-size: 2.2rem;
    margin-left: -.65em;
    margin-top:.08em;
}
.icon .float.tick{
    height:1.8rem;
    margin:0;
    top:5px;
    left: 5px;
}
.icon .text-xs{
    font-size: .75em;
    top: 60%;
    right: -5%;
}

.fc-daygrid-day-events{
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: center;
    margin: 10px;
}

.overflow {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fc .fc-daygrid-event-harness, .fc .fc-col-header-cell-cushion{ margin-top:0!important}


.flex{
    display: flex;
}
.items-center{
    align-items: center;
}
.mr-1{
    margin-right: .25rem;
}
.w-full {
    width: 100%;
}
.text-base {
    font-size: 1rem;
    line-height: 1.5rem;
    padding: 10px;
    padding-top:0;
}
.border-surface-300{
    border-color: rgb(74, 74, 82)
}
.border-b {
    border-bottom-width: 1px;
}
.px-2 {
    padding-left: .5rem;
    padding-right: .5rem;
}
.py-3 {
    padding-top: .75rem;
    padding-bottom: .75rem;
}
a {color: #333;}
.text-green-600{
    color: rgb(40 149 72)
}
.text-red-600 {
    color: rgb(220 38 38);
}
.text-orange-500 {
     color: rgb(249 115 22 / 1);
}
.bg-red-600 {
  background: rgb(253, 173, 173);
}
.bg-green-600 {
  background: rgb(83, 158, 105)
}


@media(max-width: 1280px){
    .calendar-item .flex{
        flex-wrap: wrap;
    }
    .calendar-item .text-base{
        width:100%;
    }
    .calendar-header-day {
        font-size: 1.8rem;
    }
    .calendar-header-dayname {
        font-size: 0.8rem;
    }
}
</style>